<template>
  <div class="banner-link-editor">
    <div class="mb-4">
      <label class="block text-sm font-marketing mb-2">Add Link to Text</label>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
        <input 
          v-model="linkText"
          type="text"
          placeholder="Text to link (e.g., 'click here')"
          class="px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm"
        />
        <input 
          v-model="linkUrl"
          type="url"
          placeholder="https://example.com"
          class="px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-sans text-sm"
        />
        <button 
          @click="addLink"
          :disabled="!linkText || !linkUrl"
          class="px-4 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-marketing text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          Add Link
        </button>
      </div>
      <p class="text-xs text-gray-600 font-marketing">
        Enter the exact text from your banner that should be linked, then provide the URL
      </p>
    </div>

    <div v-if="links.length > 0" class="mb-4">
      <label class="block text-sm font-marketing mb-2">Current Links</label>
      <div class="space-y-2">
        <div 
          v-for="(link, index) in links" 
          :key="index"
          class="flex items-center justify-between p-2 bg-creme-50 rounded-md border border-creme-200"
        >
          <div class="flex-1">
            <span class="text-sm font-marketing text-gray-700">
              "<strong>{{ link.text }}</strong>" → 
              <a :href="link.url" target="_blank" rel="noopener noreferrer" class="text-green-700 hover:text-green-800 underline">
                {{ link.url }}
              </a>
            </span>
          </div>
          <button 
            @click="removeLink(index)"
            class="ml-2 text-red-600 hover:text-red-700 font-marketing text-sm"
          >
            Remove
          </button>
        </div>
      </div>
    </div>

    <div v-if="bannerText" class="p-3 bg-gray-50 rounded-md border border-gray-200">
      <label class="block text-sm font-marketing mb-2">Preview</label>
      <div class="text-sm font-marketing" v-html="previewText"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'

const props = defineProps({
  bannerText: {
    type: String,
    default: ''
  },
  links: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:links'])

const linkText = ref('')
const linkUrl = ref('')
const links = ref([...props.links])

const previewText = computed(() => {
  if (!props.bannerText) return ''
  
  let result = props.bannerText
  const sortedLinks = [...links.value].sort((a, b) => b.start_index - a.start_index)
  
  for (const link of sortedLinks) {
    const before = result.substring(0, link.start_index)
    const linkText = result.substring(link.start_index, link.end_index)
    const after = result.substring(link.end_index)
    result = before + `<a href="${link.url}" target="_blank" rel="noopener noreferrer" class="link-blue">${linkText}</a>` + after
  }
  
  return result
})

const addLink = () => {
  if (!linkText.value || !linkUrl.value || !props.bannerText) {
    return
  }

  const text = props.bannerText
  const searchText = linkText.value.trim()
  const startIndex = text.indexOf(searchText)
  
  if (startIndex === -1) {
    alert(`Text "${searchText}" not found in banner text. Please enter the exact text from your banner.`)
    return
  }

  const endIndex = startIndex + searchText.length

  const newLink = {
    text: searchText,
    url: linkUrl.value.trim(),
    start_index: startIndex,
    end_index: endIndex
  }

  links.value.push(newLink)
  emit('update:links', [...links.value])
  
  linkText.value = ''
  linkUrl.value = ''
}

const removeLink = (index) => {
  links.value.splice(index, 1)
  emit('update:links', [...links.value])
}

watch(() => props.links, (newLinks) => {
  links.value = [...newLinks]
}, { deep: true })
</script>

<style scoped>
.banner-link-editor :deep(.link-blue) {
  color: #2563eb;
  text-decoration: underline;
  transition: color 0.2s;
  font-family: inherit;
}

.banner-link-editor :deep(.link-blue):hover {
  color: #1e40af;
}

.banner-link-editor :deep(.link-blue):visited {
  color: #2563eb;
}
</style>

